import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  changelogEntries,
  ChangelogEntry,
  DEFAULT_TTL_DAYS,
} from '../../../changelog/entries'
import styles from './styles.module.scss'

const STORAGE_KEY = 'vogz_whatsnew_read_v1'
// Cle utilisee pour persister la "signature" des entrees deja vues en
// pastille (collapse). Permet de NE PAS reafficher le bandeau plein a
// chaque reload tant qu il n y a pas de nouveaute. Des qu une nouvelle
// entree apparait, la signature change et le bandeau revient automa-
// tiquement deploye pour la signaler.
const COLLAPSED_STORAGE_KEY = 'vogz_whatsnew_collapsed_signature_v1'

const loadReadIds = (): Set<string> => {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return new Set(parsed.filter((v) => typeof v === 'string'))
  } catch {
    // ignore parse errors, treat as empty
  }
  return new Set()
}

const saveReadIds = (ids: Set<string>) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)))
  } catch {
    // ignore storage errors (private mode, quota...)
  }
}

const loadCollapsedSignature = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY)
  } catch {
    return null
  }
}

const saveCollapsedSignature = (signature: string | null) => {
  if (typeof window === 'undefined') return
  try {
    if (signature === null) window.localStorage.removeItem(COLLAPSED_STORAGE_KEY)
    else window.localStorage.setItem(COLLAPSED_STORAGE_KEY, signature)
  } catch {
    // ignore storage errors
  }
}

// Signature stable de la liste des entrees visibles : la marque colla-
// pse une "version" precise des nouveautes. Si une entree apparait /
// disparait, la signature change → on re-deploie le bandeau plein.
const computeSignature = (entries: { id: string }[]): string =>
  entries
    .map((e) => e.id)
    .slice()
    .sort()
    .join('|')

const isWithinTtl = (entry: ChangelogEntry, now: Date): boolean => {
  const published = new Date(entry.publishedAt)
  if (Number.isNaN(published.getTime())) return false
  const ttlDays = entry.ttlDays ?? DEFAULT_TTL_DAYS
  const expiresAt = new Date(published.getTime() + ttlDays * 24 * 60 * 60 * 1000)
  return now <= expiresAt
}

const formatDate = (iso: string): string => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
}

const renderBody = (body: string): React.ReactNode => {
  const lines = body.split('\n')
  const out: React.ReactNode[] = []
  let listBuffer: string[] = []

  const flushList = (key: string) => {
    if (listBuffer.length === 0) return
    out.push(
      <ul key={`ul-${key}`} className={styles.bodyList}>
        {listBuffer.map((item, idx) => (
          <li key={idx} dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
        ))}
      </ul>,
    )
    listBuffer = []
  }

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim()
    if (line.startsWith('- ')) {
      listBuffer.push(line.slice(2))
      return
    }
    flushList(String(idx))
    if (!line) {
      out.push(<div key={`sp-${idx}`} className={styles.bodySpacer} />)
      return
    }
    out.push(
      <p
        key={`p-${idx}`}
        className={styles.bodyParagraph}
        dangerouslySetInnerHTML={{ __html: formatInline(line) }}
      />,
    )
  })
  flushList('end')
  return out
}

const formatInline = (text: string): string => {
  // Échappement basique avant injection (titre/body sont contrôlés par nous,
  // mais on reste prudent vu qu'on utilise dangerouslySetInnerHTML).
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
}

const WhatsNewBanner: React.FC = () => {
  const [readIds, setReadIds] = useState<Set<string>>(() => loadReadIds())
  const [now, setNow] = useState<Date>(() => new Date())
  const [openEntryId, setOpenEntryId] = useState<string | null>(null)
  const [collapsed, setCollapsedState] = useState(false)

  // Recalcule le TTL une fois par heure pour fiabiliser l'affichage sur les
  // sessions longues (utilisateur qui laisse l'onglet ouvert).
  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 60 * 60 * 1000)
    return () => window.clearInterval(interval)
  }, [])

  const visibleEntries = useMemo(() => {
    return changelogEntries
      .filter((entry) => !readIds.has(entry.id))
      .filter((entry) => isWithinTtl(entry, now))
      .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
  }, [readIds, now])

  const signature = useMemo(() => computeSignature(visibleEntries), [visibleEntries])

  // Wrapper qui persiste le state collapse en meme temps que le state React.
  // - collapsed=true → on memorise la signature courante : tant que les
  //   nouveautes ne bougent pas, on reste discret en pastille
  // - collapsed=false → on efface la cle (nouvelle interaction explicite)
  const setCollapsed = useCallback(
    (next: boolean) => {
      setCollapsedState(next)
      saveCollapsedSignature(next ? signature : null)
    },
    [signature],
  )

  // Au mount + quand la signature change, on compare a celle persistee.
  // Si pareille → l user a deja ferme cette serie de nouveautes, on
  // demarre directement en pastille collapsed (silencieux).
  // Si differente → nouvelle entree, on re-deploie le bandeau et on
  // nettoie l ancienne signature persistee.
  useEffect(() => {
    if (signature === '') {
      setCollapsedState(false)
      return
    }
    const stored = loadCollapsedSignature()
    if (stored === signature) {
      setCollapsedState(true)
    } else {
      setCollapsedState(false)
      if (stored) saveCollapsedSignature(null)
    }
  }, [signature])

  const openEntry = useMemo(
    () => visibleEntries.find((e) => e.id === openEntryId) ?? null,
    [visibleEntries, openEntryId],
  )

  const markAsRead = useCallback((id: string) => {
    setReadIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      saveReadIds(next)
      return next
    })
    setOpenEntryId((current) => (current === id ? null : current))
  }, [])

  const handleOpen = useCallback((id: string) => {
    setOpenEntryId(id)
    setCollapsed(false)
  }, [])

  const handleClose = useCallback(() => setOpenEntryId(null), [])

  if (visibleEntries.length === 0) return null

  const headline = visibleEntries[0]
  const extraCount = visibleEntries.length - 1

  return (
    <>
      {!collapsed && (
        <button
          type="button"
          className={styles.banner}
          onClick={() => handleOpen(headline.id)}
          aria-label={`Voir la nouveauté : ${headline.title}`}
        >
          <span className={styles.bannerEmoji} aria-hidden="true">
            {headline.emoji ?? '✨'}
          </span>
          <span className={styles.bannerContent}>
            <span className={styles.bannerHead}>
              <span className={styles.bannerBadge}>Nouveau</span>
              <span className={styles.bannerTitle}>{headline.title}</span>
            </span>
            <span className={styles.bannerSummary}>{headline.summary}</span>
          </span>
          {extraCount > 0 && (
            <span className={styles.bannerCount} aria-label={`${extraCount} autres nouveautés`}>
              +{extraCount}
            </span>
          )}
          <span
            className={styles.bannerClose}
            role="button"
            tabIndex={0}
            aria-label="Réduire le bandeau"
            onClick={(e) => {
              e.stopPropagation()
              setCollapsed(true)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                setCollapsed(true)
              }
            }}
          >
            ×
          </span>
        </button>
      )}

      {collapsed && (
        <button
          type="button"
          className={styles.pill}
          onClick={() => setCollapsed(false)}
          aria-label="Voir les nouveautés"
        >
          <span aria-hidden="true">✨</span>
          <span className={styles.pillCount}>{visibleEntries.length}</span>
        </button>
      )}

      {openEntry && (
        <div
          className={styles.drawerOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="whatsnew-title"
          onClick={handleClose}
        >
          <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <header className={styles.drawerHeader}>
              <div className={styles.drawerTitleBlock}>
                <span className={styles.drawerEmoji} aria-hidden="true">
                  {openEntry.emoji ?? '✨'}
                </span>
                <div>
                  <div className={styles.drawerKicker}>
                    Nouveauté · {formatDate(openEntry.publishedAt)}
                  </div>
                  <h3 id="whatsnew-title" className={styles.drawerTitle}>
                    {openEntry.title}
                  </h3>
                </div>
              </div>
              <button
                type="button"
                className={styles.drawerClose}
                onClick={handleClose}
                aria-label="Fermer"
              >
                ×
              </button>
            </header>

            <div className={styles.drawerBody}>{renderBody(openEntry.body)}</div>

            {visibleEntries.length > 1 && (
              <div className={styles.drawerOthers}>
                <div className={styles.drawerOthersLabel}>Autres nouveautés récentes</div>
                <ul className={styles.drawerOthersList}>
                  {visibleEntries
                    .filter((e) => e.id !== openEntry.id)
                    .map((e) => (
                      <li key={e.id}>
                        <button
                          type="button"
                          className={styles.drawerOtherItem}
                          onClick={() => handleOpen(e.id)}
                        >
                          <span aria-hidden="true">{e.emoji ?? '✨'}</span>
                          <span>{e.title}</span>
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            <footer className={styles.drawerFooter}>
              <button
                type="button"
                className={styles.drawerPrimary}
                onClick={() => markAsRead(openEntry.id)}
              >
                C'est compris
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  )
}

export default WhatsNewBanner
